import type { IconNode } from 'lucide';
import { ChevronDown, ChevronRight, CircleCheck, CircleX, Eye } from 'lucide';
import type { CellComponent } from 'tabulator-tables';
import { TabulatorFull as Tabulator } from 'tabulator-tables';

export function datesSorter(a: string, b: string): number {
    return new Date(a).getTime() - new Date(b).getTime();
}

export function uniqueValues(data: Record<string, unknown>[], field: string): string[] {
    const values = data
        .map((row) =>
            field
                .split('.')
                .reduce(
                    (o: unknown, key: string) =>
                        o == null ? undefined : (o as Record<string, unknown>)[key],
                    row,
                ),
        )
        .filter((value) => value !== undefined && value !== null && value !== '');
    return [...new Set(values as string[])].sort();
}

export interface SelectOption {
    value: string;
    label: string;
}

export function toOptions(values: string[]): SelectOption[] {
    return values.map((v) => ({ value: v, label: v }));
}

export function populateSelect(select: HTMLSelectElement, options: SelectOption[]): void {
    select.innerHTML =
        '<option value="">All</option>' +
        options.map((o) => `<option value="${o.value}">${o.label}</option>`).join('');
}

const iconNodes: Record<string, IconNode> = {
    CircleCheck,
    CircleX,
    Eye,
    ChevronDown,
    ChevronRight,
};

export function icon(name: string): string {
    const key = name.replace(/(^|-+)([a-z])/g, (_, _sep, c: string) => c.toUpperCase());
    const nodes = iconNodes[key];
    if (!nodes) return '';
    const children = nodes
        .map(([tag, attrs]) => {
            const attrStr = Object.entries(attrs)
                .map(([k, v]) => `${k}="${v}"`)
                .join(' ');
            return `<${tag} ${attrStr}/>`;
        })
        .join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${children}</svg>`;
}

export function linkFormatter(cell: CellComponent): string {
    return `<a href="${cell.getValue()}" class="inline-flex items-center border border-primary text-primary hover:bg-primary hover:text-white text-sm py-1 px-2 rounded-md transition-colors">View</a>`;
}

export function readData<T = Record<string, unknown>>(id: string): T[] {
    return JSON.parse(document.getElementById(id)!.textContent!);
}

export interface Filter {
    select: HTMLSelectElement | HTMLInputElement;
    field: string;
    predicate?: (filterValue: string, rowValue: unknown) => boolean;
}

export function wireFilters(table: Tabulator, filters: Filter[]): void {
    function applyFilters(): void {
        table.setFilter((data: Record<string, unknown>) =>
            filters.every((f) => {
                const val = (f.select as HTMLInputElement).value;
                if (!val) return true;
                const rowVal = f.field
                    .split('.')
                    .reduce(
                        (o: unknown, key) =>
                            o == null ? undefined : (o as Record<string, unknown>)[key],
                        data,
                    );
                return f.predicate ? f.predicate(val, rowVal) : String(rowVal) === val;
            }),
        );
    }
    filters.forEach((f) => {
        f.select.addEventListener('change', applyFilters);
        f.select.addEventListener('input', applyFilters);
    });
}
